import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Modal,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import * as yup from "yup";
import { Form, Formik } from "formik";
import { AxiosResponse } from "axios";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../Common/styles/modal";
import isEmail from "validator/es/lib/isEmail";
import { DrawerForm } from "../Common/styles/drawer";

const schema = yup.object().shape({
  firstName: yup.string().required("First Name is Required"),
  lastName: yup.string().required("Last Name is Required"),
  email: yup
    .string()
    .required("Email is Required")
    .test("is-valid", "Please enter a valid Email ID.", (value) =>
      value ? isEmail(value) : false
    ),
  sendEmail: yup.boolean(),
});

type Props = {
  showModal: boolean;
  closeModal: () => void;
  refreshPage: () => void;
};

const AddParticipantModal = ({ showModal, closeModal, refreshPage }: Props) => {
  const [submitLoader, setSubmitLoader] = useState(false);

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const body = {
        firstName: values?.firstName,
        lastName: values?.lastName,
        email: values?.email,
        sendEmail: values?.sendEmail ?? true,
      };

      const res: AxiosResponse = await http.post(
        `/admin/registry/participants`,
        body
      );

      toastMessage(
        "success",
        res?.data?.message || "Participant added successfully"
      );
      closeModal();
      setSubmitLoader(false);
      refreshPage();
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader title="Add Participant" onCloseClick={closeModal} />
        <Formik
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
            sendEmail: true,
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched, setFieldValue, values }) => (
            <Form style={DrawerForm}>
              <Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle} htmlFor="first-name">
                      First Name <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <TextField
                      placeholder="First name"
                      id="first-name"
                      {...getFieldProps("firstName")}
                      error={
                        touched?.firstName && errors?.firstName ? true : false
                      }
                      helperText={
                        touched?.firstName && errors?.firstName
                          ? (errors?.firstName as string)
                          : " "
                      }
                    />
                  </FormControl>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle} htmlFor="last-name">
                      Last Name <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <TextField
                      placeholder="Last name"
                      id="last-name"
                      {...getFieldProps("lastName")}
                      error={
                        touched?.lastName && errors?.lastName ? true : false
                      }
                      helperText={
                        touched?.lastName && errors?.lastName
                          ? (errors?.lastName as string)
                          : " "
                      }
                    />
                  </FormControl>
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle} htmlFor="email">
                      Email <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <TextField
                      placeholder="Email"
                      id="email"
                      {...getFieldProps("email")}
                      error={touched?.email && errors?.email ? true : false}
                      helperText={
                        touched?.email && errors?.email
                          ? (errors?.email as string)
                          : " "
                      }
                    />
                  </FormControl>
                </Box>
                <FormControl sx={InputWrapper}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values?.sendEmail === true}
                        onChange={(event) => {
                          setFieldValue("sendEmail", event.target.checked);
                        }}
                      />
                    }
                    label="Send email invite"
                  />
                </FormControl>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!submitLoader ? (
                    <>
                      <Button onClick={closeModal} variant="outlined">
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained">
                        Save
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddParticipantModal;
